import dayjs, { type Dayjs } from 'dayjs';
import numeral from 'numeral';
import 'numeral/locales/de';
import 'numeral/locales/nl-nl';

import { getCurrentLang } from '@/lib/i18n/getCurrentLang';
import { roundTonneValue } from '@/utils/number';

import { NULL_DATES } from './date';

import type { DateRange, SupportedLanguage } from './globalTypes';
import type { TFunction } from 'next-i18next/pages';

const NULL = '0';
const NUM = '0,0[.]00'; // e.g. "1,234.56"
const NUMACT3 = '0,0[.]00'; // with thousands separator & decimals

/** Formats a number with thousands separator and decimals */
export function formatNum(
  num: number | null | undefined,
  lang: SupportedLanguage,
  options: Intl.NumberFormatOptions = {}
): string {
  if (num == null || isNaN(num)) return NULL;
  return num?.toLocaleString(lang, {
    maximumFractionDigits: 2,
    ...options,
  });
}

/** Global number format without compact notation */
export function formatNumGlobal(num: number | null | undefined): string {
  return num != null && !isNaN(num) ? numeral(num).format(NUM) : NULL;
}

/** Convert kilos to tonnes with threshold check */
export function kiloToTonne(kilos: number): string {
  const lang = getCurrentLang();
  if (!kilos) return NULL;
  const tonnes = kilos * 0.001;
  if (Math.abs(kilos) < 10) return `< ${(0.01).toLocaleString(lang, { maximumFractionDigits: 2 })} t`;
  return `${tonnes.toLocaleString(lang, { maximumFractionDigits: 2 })} t`;
}

/** Convert percentage with threshold check */
export function toPercent(num: number): string {
  const lang = getCurrentLang();
  if (!num) return NULL;
  if (Math.abs(num) < 0.1) return `< ${(0.1).toLocaleString(lang, { maximumFractionDigits: 2 })}%`;
  return `${num.toLocaleString(lang, { maximumFractionDigits: 2 })}%`;
}

/** Convert kilos to tonnes in raw number format */
export function toTonneNum(num: number | null | undefined): string {
  return num != null ? numeral(num * 0.001).format(NUM) : NULL;
}

/** Format tonne value with suffix */
export function toFormattedTonne(tonne: number): string {
  return `${numeral(tonne).format(NUMACT3)} t`;
}

/** Round and format tonne value */
export function toFormattedRoundTonneValue(tonneValue: number): string {
  if (tonneValue < 1) return '1 t';
  const roundedValue = roundTonneValue(tonneValue);
  return `${numeral(roundedValue).format(NUMACT3)} t`;
}

/** Format camelCase or snake_case into readable string */
export function formatCamelCase(str: string): string {
  return str
    .replace(/_/g, ' - ') // underscores
    .replace(/([A-Z])/g, ' $1') // camelCase split
    .trim();
}

/** Trim a string to a specific length */
export function trimString(str: string, len: number = 50): string {
  return str.length > len ? `${str.substring(0, len)}...` : str;
}

/** Capitalize the first letter of a string */
export function capitalize(str: string): string {
  return str.charAt(0).toUpperCase() + str.slice(1);
}

/** Convert a Dayjs date range to { month, year } objects */
export function adaptDateRange(dateRange: [Dayjs, Dayjs] | null): DateRange {
  if (dateRange && dateRange[0] && dateRange[1]) {
    const start = { month: dateRange[0].month() + 1, year: dateRange[0].year() };
    const end = { month: dateRange[1].month() + 1, year: dateRange[1].year() };
    return [start, end];
  }
  return [];
}

/** Convert [minDate, maxDate] into Jan through max month of the max year. */
export function toLatestYearDateRange(dateRangeMinMax?: DateRange): DateRange {
  const maxDate = dateRangeMinMax?.[1];
  if (!maxDate) return [];

  const maxYear = Number(maxDate.year);
  const maxMonth = Number(maxDate.month);

  if (!Number.isFinite(maxYear) || !Number.isFinite(maxMonth) || maxMonth < 1 || maxMonth > 12) return [];

  return [
    { month: 1, year: maxYear },
    { month: maxMonth, year: maxYear },
  ];
}

export function isLatestYearPeriod(dateRange?: DateRange, maxRange?: DateRange) {
  if (!Array.isArray(dateRange) || !Array.isArray(maxRange)) return false;
  if (dateRange.length < 2 || maxRange.length < 2) return false;

  const [start, end] = dateRange;
  const max = maxRange[1];

  return start.month === 1 && start.year === end.year && end.year === max.year && end.month === max.month;
}

/** Convert DateRange back to Dayjs objects */
export function reverseDateRange(dateRange: DateRange): [Dayjs, Dayjs] | [] {
  if (dateRange.length === 2) {
    // build from day 1 so the month's end isn't shifted by today's day of month
    const toMonthStart = ({ year, month }: { year: number; month: number }) => dayjs(new Date(year, month - 1, 1));

    return [toMonthStart(dateRange[0]), toMonthStart(dateRange[1]).endOf('month')];
  }
  return NULL_DATES;
}

/** Localizes a numeric value, parsing numeric strings first (non-numeric strings are returned as-is) */
function toLocaleNumber(value: number | string, lang?: SupportedLanguage): string {
  const num = typeof value === 'string' ? Number(value) : value;
  if (typeof value === 'string' && (value.trim() === '' || isNaN(num))) return value;

  return num.toLocaleString(lang ?? getCurrentLang(), { maximumFractionDigits: 2 });
}

/** Convert distance in km to localized compact format */
export function toLocaleKm(distanceKm: number | string, lang?: SupportedLanguage): string {
  return toLocaleNumber(distanceKm, lang);
}

export function toLocaleKg(weightKg: number | string, lang?: SupportedLanguage): string {
  return toLocaleNumber(weightKg, lang);
}

/** Convert kilos to tonne value (raw number as string) */
export function kiloToTonneValue(amount: number): string {
  return (amount * 0.001).toFixed(2);
}

/**
 * Converts a date range into a human-readable relative period label.
 *
 * Handles common cases such as:
 * - Previous calendar year → "Last year"
 * - Previous calendar month → "Last month"
 * - Multi-month / multi-year ranges → "Last X years and Y months"
 *
 * @param dateRange - A tuple containing start and end dates with `{ year, month }`
 * @returns A relative period label, or an empty string if the range is invalid
 */
export function toPeriodLabel({ dateRange, t }: { dateRange: DateRange; t: TFunction }) {
  // destructure and validate upfront
  if (!Array.isArray(dateRange) || dateRange.length < 2) return '';

  const [start, end] = dateRange;
  const now = new Date();
  const previousYear = now.getFullYear() - 1;
  const lastMonthDate = new Date(now.getFullYear(), now.getMonth() - 1, 1); // rolls back into December in January
  const lastMonth = { month: lastMonthDate.getMonth() + 1, year: lastMonthDate.getFullYear() };

  const toTotalMonths = ({ year, month }: { year: number; month: number }) => year * 12 + (month - 1);
  const diffMonths = toTotalMonths(end) - toTotalMonths(start) + 1;
  const years = Math.floor(diffMonths / 12);
  const months = diffMonths % 12;

  // special cases: previous calendar year / previous month
  if (start.month === 1 && end.month === 12 && start.year === previousYear && end.year === previousYear) {
    return capitalize(t('common:period.lastYear'));
  }

  if (
    start.month === end.month &&
    start.month === lastMonth.month &&
    start.year === end.year &&
    start.year === lastMonth.year
  ) {
    return capitalize(t('common:period.lastMonth'));
  }

  if (!years) {
    return capitalize(t('common:period.lastMonth', { amount: months, count: months }));
  }

  if (!months) {
    return capitalize(t('common:period.lastYear', { amount: years, count: years }));
  }

  return capitalize(
    t('common:period.lastYearsAndMonths', {
      months: t('common:period.monthUnit', { amount: months, count: months }),
      years: t('common:period.yearUnit', { amount: years, count: years }),
    })
  );
}

/**
 * Formats a date range into a short, locale-agnostic string.
 *
 * Output format:
 * `MMM, YYYY - MMM, YYYY` (e.g. "Jan, 2023 - Mar, 2024")
 *
 * @param dateRange - A tuple containing start and end dates with `{ year, month }`
 * @param locale - Optional BCP 47 locale string (e.g. "en-US", "de-DE"). Falls back to `"en-US"` if not provided.
 * @returns A formatted date range string
 */
export function toFormattedDateRange(dateRange: DateRange, locale = 'en-US'): string {
  const [start, end] = dateRange;
  const monthFormatter = new Intl.DateTimeFormat(locale, { month: 'short' });

  const startDate = `${monthFormatter.format(new Date(start.year, start.month - 1))}, ${start.year}`;
  const endDate = `${monthFormatter.format(new Date(end.year, end.month - 1))}, ${end.year}`;

  return `${startDate} - ${endDate}`;
}

function toDate(date: string | Date) {
  if (typeof date !== 'string') return date;

  const [month, year] = date.split('/');

  return new Date(`20${year}-${month}-01`);
}

/**
 * Converts a date string in the format `"MM/YY"` to a short localized date string.
 *
 * @param {string} date - The date string in `"MM/YY"` format.
 * @param options - Controls whether the year is included in the formatted result.
 * @returns {string} A formatted short date string using the specified locale.
 *
 * @example
 * toShortDateString("01/21"); // "Jan/21"
 * toShortDateString("12/24", { includeYear: false }); // "Dec"
 */
export function toShortDateString(date: string | Date, { includeYear = true }: { includeYear?: boolean } = {}) {
  const lang = getCurrentLang();
  const fullDate = toDate(date);

  const formatter = new Intl.DateTimeFormat(lang, {
    month: 'short',
    ...(includeYear && { year: '2-digit' }),
  });

  return capitalize(formatter.format(fullDate).replace(' ', includeYear ? '/' : ''));
}

export function toLongDateString(date: string | Date) {
  const fullDate = toDate(date);
  const month = new Intl.DateTimeFormat(getCurrentLang(), { month: 'short' }).format(fullDate);

  return `${capitalize(month)}/${fullDate.getFullYear()}`;
}

export function toMonthYearString(date: string | Date) {
  const fullDate = toDate(date);
  const month = new Intl.DateTimeFormat(getCurrentLang(), { month: 'short' }).format(fullDate);

  return `${capitalize(month)}, ${fullDate.getFullYear()}`;
}

/**
 * @returns {string} "CODE - NAME" when both are provided, or the one that exists.
 * Otherwise returns empty string ('').
 * @example toEuralString(123, "EWC label"); // "123 - EWC label"
 */
export const toEuralLabel = (euralCode?: string, euralName?: string): string => {
  if (euralCode && euralName) return `${euralCode} - ${euralName}`;
  return euralCode || euralName || '';
};

/**
 * Converts a date string in the format `"MM/YY"` to a `DateRange`,
 *
 * @param period "MM/YY"
 * @returns DateRange  [{ month, year }, { month, year }]
 */
export function toDateRange(period: string): DateRange {
  const [monthStr, yearStr] = period.split('/');
  const month = parseInt(monthStr, 10);
  const year = 2000 + parseInt(yearStr, 10); // assumes 2000s

  return [
    { month, year },
    { month, year },
  ];
}

/**
 * Unit is the origin value (what's being converted from)
 * it can be kt to kt (just add kt at the end)
 * or can be from kg value to kt (convert and add kt at the end)
 */
export const toKiloTonne = (num: number, unit = 'kg', lang = 'en') => {
  const converter = unit == 'kg' ? 10 ** 6 : 1;
  const value = Math.abs(num) / converter;
  const formattedValue =
    value < 0.01
      ? '< 0.01'
      : value.toLocaleString(lang, {
          maximumFractionDigits: 2,
        });
  return `${formattedValue} kt`;
};

export function formatStringDate(stringDate: string) {
  const date = new Date(stringDate);
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();
  return `${day}/${month}/${year}`;
}
